import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Headphones, Loader2, Send } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FAQS = [
  { q: "How do tasks work?", a: "Browse the Tasks page, open any active task and read its instructions. Install the app from the Google Play Store, explore it as instructed, then submit your honest feedback — often with a screenshot as proof. Each task shows its reward, estimated time and remaining slots." },
  { q: "How do I submit a task?", a: "Open a task and tap SUBMIT TASK. Your name, User ID and task details are filled in automatically. Write your genuine feedback, attach a screenshot of the app if requested, and submit. You'll get a unique Submission ID to track it." },
  { q: "When will my reward be credited?", a: "Submissions are reviewed by our team, usually within 24–72 hours. Once verified, the reward is credited to your balance instantly and appears in your Payments page. Remember: rewards are for completing the research task honestly — never for giving a particular rating or positive review." },
  { q: "How do I withdraw?", a: "Go to Payments, enter your UPI ID and an amount above the minimum withdrawal limit, then tap Request Withdrawal. Our team processes payouts to your UPI ID and the status (Pending → Processing → Paid) is shown in your withdrawal history." },
  { q: "What happens if my submission is rejected?", a: "You'll see the exact rejection reason in Account → My Submissions. Common reasons include unclear screenshots or feedback that doesn't reflect genuine app usage. If resubmission is enabled for that task, you can submit again." },
  { q: "How do I update my UPI ID?", a: "Go to Account → Profile Information, update your UPI ID and tap Save Changes. Your new UPI ID will be used for future withdrawals." },
];

export default function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/support", form);
      toast.success(data.message || "Support request submitted");
      setForm((f) => ({ ...f, subject: "", message: "" }));
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  return (
    <div className="max-w-3xl mx-auto space-y-10" data-testid="support-page">
      <div className="text-center animate-fade-up">
        <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center mx-auto shadow-lg">
          <Headphones className="w-7 h-7" />
        </div>
        <h1 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading" data-testid="support-heading">How can we help?</h1>
        <p className="mt-3 text-base sm:text-lg text-slate-600">Find quick answers below, or send us a message — we usually reply within 24 hours.</p>
      </div>

      <section data-testid="faq-section">
        <h2 className="text-xl sm:text-2xl font-bold font-heading text-slate-900">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="mt-5 space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200/80 rounded-2xl px-5 shadow-sm">
              <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-semibold text-slate-900 text-base py-4">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-7" data-testid="contact-support-card">
        <h2 className="text-xl font-bold font-heading text-slate-900">Contact Support</h2>
        <p className="mt-1.5 text-sm text-slate-600">Tell us what's going on and our team will get back to you.</p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Name</label>
              <input data-testid="support-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inCls} placeholder="Your name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input data-testid="support-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inCls} placeholder="you@example.com" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject</label>
            <input data-testid="support-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inCls} placeholder="e.g. Question about my withdrawal" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Message</label>
            <textarea data-testid="support-message-input" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inCls} placeholder="Describe your issue or question in detail" />
          </div>
          <button type="submit" data-testid="support-submit-btn" disabled={loading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Request
          </button>
        </form>
      </section>
    </div>
  );
}
